import { ChevronRight, Timer } from "lucide-react";
import { Button } from "../ui/button";

export interface ModuleIntroModalProps {
  title: string;
  subtitle: string;
  description: string;
  instructions: string[];
  /** Tip amigable bajo las instrucciones (opcional). */
  tip?: string;
  tags: string[];
  timeEstimateMin: number;
  /** Tempo visible arriba (ahorra espacio en tags). */
  bpm?: number;
  /** Contador circular de pasos (derecha, bajo el tiempo). */
  stepProgress?: { current: number; total: number };
  accent: string;
  accentSoft: string;
  accentGlow: string;
  borderHover: string;
  textMain: string;
  textMuted: string;
  textDim: string;
  wasCompletedBefore?: boolean;
  beginLabel?: string;
  onBegin: () => void;
}

function StepCircle({
  current,
  total,
  accent,
}: {
  current: number;
  total: number;
  accent: string;
}) {
  const size = 44;
  const stroke = 3.5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const progress = total > 0 ? Math.min(1, current / total) : 0;
  const offset = circ * (1 - progress);

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      role="img"
      aria-label={`Paso ${current} de ${total}`}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums"
        style={{ color: accent }}
      >
        {current}
        <span className="opacity-45">/{total}</span>
      </span>
    </div>
  );
}

export function ModuleIntroModal({
  title,
  subtitle,
  description,
  instructions,
  tip,
  tags,
  timeEstimateMin,
  bpm,
  stepProgress,
  accent,
  accentSoft,
  accentGlow,
  borderHover,
  textMain,
  textMuted,
  textDim,
  wasCompletedBefore = false,
  beginLabel,
  onBegin,
}: ModuleIntroModalProps) {
  return (
    <div className="absolute inset-0 z-40 flex items-center justify-center p-4 sm:p-8 module-intro-enter">
      <div className="absolute inset-0 bg-black/62" aria-hidden />

      <article
        role="dialog"
        aria-labelledby="module-intro-title"
        className="relative z-10 w-full max-w-2xl max-h-[min(92vh,720px)] overflow-y-auto rounded-2xl p-5 sm:p-7 shadow-2xl module-intro-card"
        style={{
          background: "linear-gradient(165deg, rgba(42,38,36,0.97) 0%, rgba(28,26,25,0.98) 100%)",
          border: `1px solid ${borderHover}`,
          boxShadow: `0 24px 80px rgba(0,0,0,0.55), 0 0 0 1px ${accentSoft}, inset 0 1px 0 rgba(255,255,255,0.06)`,
        }}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Tag key={tag} accentSoft={accentSoft} accent={accent}>
                {tag}
              </Tag>
            ))}
          </div>
          {bpm != null && (
            <span
              className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] tabular-nums"
              style={{ color: accent }}
            >
              {bpm} BPM
            </span>
          )}
        </div>

        <h2 id="module-intro-title" className="text-lg font-bold mb-1" style={{ color: textMain }}>
          {title}
        </h2>
        <p className="text-xs mb-3" style={{ color: textDim }}>
          {subtitle}
        </p>

        {description ? (
          <p className="text-sm leading-relaxed mb-4" style={{ color: textMuted }}>
            {description}
          </p>
        ) : null}

        <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: textDim }}>
          Instrucciones
        </p>
        <ul className="space-y-2 mb-4">
          {instructions.map((inst, i) => (
            <li key={i} className="flex items-start gap-2.5 text-sm" style={{ color: textMuted }}>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 mt-0.5"
                style={{ backgroundColor: accentSoft, color: accent }}
              >
                {i + 1}
              </span>
              {inst}
            </li>
          ))}
        </ul>

        {tip ? (
          <p
            className="mb-5 rounded-xl border px-3.5 py-3 text-sm leading-relaxed"
            style={{
              borderColor: "rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.03)",
              color: textMuted,
            }}
          >
            <span
              className="mb-1 block text-[10px] font-bold uppercase tracking-wider"
              style={{ color: accent }}
            >
              Tip
            </span>
            {tip}
          </p>
        ) : null}

        <div className="mb-5 flex justify-end">
          <div className="flex flex-col items-end gap-1.5">
            <p className="flex items-center gap-2 text-[10px]" style={{ color: textDim }}>
              <Timer className="h-3.5 w-3.5 shrink-0" />~{timeEstimateMin} min
            </p>
            {stepProgress && stepProgress.total > 0 && (
              <StepCircle
                current={stepProgress.current}
                total={stepProgress.total}
                accent={accent}
              />
            )}
          </div>
        </div>

        <Button
          onClick={onBegin}
          className="w-full font-semibold text-white border-0 h-11"
          style={{ backgroundColor: accent, boxShadow: `0 8px 28px ${accentGlow}` }}
          size="lg"
        >
          {beginLabel ?? (wasCompletedBefore ? "Repetir lección" : "Comenzar lección")}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </article>
      <style>{`
        .module-intro-enter { animation: moduleIntroFade 0.28s ease-out both; }
        .module-intro-card { animation: moduleIntroCard 0.34s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @keyframes moduleIntroFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes moduleIntroCard {
          from { opacity: 0; transform: translateY(18px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}

function Tag({
  children,
  accent,
  accentSoft,
}: {
  children: React.ReactNode;
  accent: string;
  accentSoft: string;
}) {
  return (
    <span
      className="text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{ backgroundColor: accentSoft, color: accent }}
    >
      {children}
    </span>
  );
}
